import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {

  private spinnerSubject = new BehaviorSubject(false)

  showSpinner = this.spinnerSubject.asObservable()

  constructor(
    private _snackBar: MatSnackBar
  ) { }

  openSnackBar(message: string, action: string, styleClass: string = '') {
    const snackBarConfig: any = {
      duration: 5000
    };

    const sanitizedStyleClass = (styleClass || '').trim();
    if (sanitizedStyleClass) {
      snackBarConfig.panelClass = [sanitizedStyleClass];
    }

    this._snackBar.open(message, action, snackBarConfig);
  }

  setSpinner(val: any) {
    this.spinnerSubject.next(val)
  }

  getAllLocationDetails(placeObject) {
    let city = ""
    let state = ""
    let country = ""
    let zipcode = ""
    let address = ""
    let locationObject = {}
    placeObject.address_components.forEach(element => {
      let addressType = element.types
      if (addressType.indexOf("street_number") >= 0) {
        address += element.long_name
      } else if (addressType.indexOf("route") >= 0) {
        if (address != '') {
          address += ", " + element.long_name
        } else {
          address += element.long_name
        }
      } else if (addressType.indexOf("locality") >= 0) {
        if (address != '') {
          address += ", " + element.long_name
        } else {
          address += element.long_name
        }
      } else if (addressType.indexOf("administrative_area_level_2") >= 0) {
        city = element.long_name
      } else if (addressType.indexOf("administrative_area_level_1") >= 0) {
        state = element.long_name
      } else if (addressType.indexOf("country") >= 0) {
        country = element.long_name
      } else if (addressType.indexOf("postal_code") >= 0) {
        zipcode = element.long_name
      }
    })
    locationObject = {
      city: city,
      state: state,
      country: country,
      zipcode: zipcode,
      address: address,
      latitude: placeObject.geometry.location.lat(),
      longitude: placeObject.geometry.location.lng(),
      completeString: placeObject.formatted_address
    }
    return locationObject
  }

  /**
 * Get URL parameters
 */
  getURLData() {
    let userType = '';
    let lastOne = '';
    let lastTwo = '';
    let lastThird = '';
    let returnData: any = {};
    const locationArray = location.href.split("/");
    lastOne = locationArray[locationArray.length - 1];
    lastTwo = locationArray[locationArray.length - 2];
    lastThird = locationArray[locationArray.length - 3];

    returnData = {
      'lastOne': lastOne,
      'lastTwo': lastTwo,
      'lastThird': lastThird
    }
    return returnData;
  }

  //method to find initials of fullname
  findInitials() {
    let name
    name = localStorage.getItem("endUserFullName");
    // let rgx = new RegExp(/(\p{L}{1})\p{L}+/, 'gu');
    let rgx = new RegExp(/\p{L}{1}\p{L}+/gu);

    let initials = [...name.matchAll(rgx)];

    initials = (
      (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
    ).toUpperCase();
    return initials
  }

  //method to capitalise first letter of word
  capitalizeFirstLetter(word){
    return word.charAt(0).toUpperCase() + word.slice(1);
  }

  /**
   * Select the role shown beside a participant in admin case chat.
   *
   * Hospital admins can also have a clinical staffRole (for example DVM), so
   * staffRole must not be limited to hospitalRole === 'staff'. Broader labels
   * are only fallbacks for older hospital users without that metadata.
   */
  getCaseChatRoleLabel(user: any): string {
    if (!user) {
      return '';
    }

    const userType = String(user.userType || '').trim().toLowerCase();
    if (userType === 'admin') {
      return 'Admin';
    }

    const hospitalRole = String(user.hospitalRole || '').trim().toLowerCase();
    const staffRole = String(user.staffRole || '').trim();
    const isHospitalUser = ['pcv', 'vc', 'hospital'].includes(userType)
      || hospitalRole.includes('admin')
      || hospitalRole.includes('staff');

    if (isHospitalUser && staffRole) {
      return staffRole;
    }
    if (hospitalRole.includes('admin')) {
      return 'Hospital Admin';
    }
    if (hospitalRole.includes('staff')) {
      return 'Hospital Staff';
    }
    if (userType === 'pcv') {
      return 'PCV';
    }
    if (userType === 'vc') {
      return 'VC';
    }
    if (userType === 'hospital') {
      return 'Hospital';
    }

    return '';
  }

  /**
   * Whether a case's first final report has been published.
   * Mirrors the backend predicate (helpers/reportPublication.js): true when the
   * appointment has currentReportVersion >= 1, a populated lastReportFinalizedAt,
   * a non-empty reportHistory, or a non-empty final report filename in `report`.
   * Used to hide/disable the Cancel action once a report of record exists.
   */
  hasPublishedFinalReport(caseItem: any): boolean {
    if (!caseItem) { return false; }
    const versionPublished = typeof caseItem.currentReportVersion === 'number' && caseItem.currentReportVersion >= 1;
    const finalizedAtSet = !!caseItem.lastReportFinalizedAt;
    const historyExists = Array.isArray(caseItem.reportHistory) && caseItem.reportHistory.length > 0;
    const reportFileSet = !!caseItem.report && String(caseItem.report).trim() !== '';
    return versionPublished || finalizedAtSet || historyExists || reportFileSet;
  }

  getFinalReportCancellationTooltip(caseItem: any, userType: string = ''): string {
    const standardTooltip = 'A case cannot be cancelled once the final report has been published. If you feel that this case is complete, please close the case.';
    const avTooltip = 'A case cannot be cancelled once the final report has been published. If you feel that this case is complete, please close the case. Once a case is “closed,” you will not be able to join the scheduled video call.';
    const pcvTooltip = 'This case cannot be cancelled because the final report has already been published. Please use the chat box on the case page if you have any questions.';

    if (userType.toLowerCase() === 'pcv') {
      return pcvTooltip;
    }

    return caseItem?.modality === 'audio/video' ? avTooltip : standardTooltip;
  }
}
